import { useEffect, useMemo, useState } from 'react';
import { isNil } from 'lodash';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { getCashFlowSummaryColumns } from '../config/CashFlowSummaryColumnConfig';
import { CashFlowSummaryParser } from '../config/CashFlowSummaryParser';
import { CashFlowSummaryRowConfig } from '../config/CashFlowSummaryRowConfig';
import type {
  CashFlowSummaryCellData,
  CashFlowSummaryColumnDefinition,
  CashFlowSummaryInitialObject,
  CashFlowSummaryRowData,
  CashFlowSummaryTableData,
} from '../config/types';

export interface UseCashFlowSummarySpreadsheetConfigProps {
  fundsGrossIrr: Record<string, number | null>;
  firmGrossIrr: number | null;
  fundsInvestments: Record<string, number>;
  fundsProceeds: Record<string, number>;
  firmInvestments: number;
  firmProceeds: number;
  fundIdToName: Record<number, string>;
  firmName: string;
}

type CashFlowSummarySpreadsheet = SpreadsheetConfig<
  CashFlowSummaryInitialObject,
  CashFlowSummaryColumnDefinition,
  CashFlowSummaryTableData,
  CashFlowSummaryCellData
>;

export const useCashFlowSummarySpreadsheetConfig = ({
  fundsGrossIrr,
  firmGrossIrr,
  fundsInvestments,
  fundsProceeds,
  firmInvestments,
  firmProceeds,
  fundIdToName,
  firmName,
}: UseCashFlowSummarySpreadsheetConfigProps) => {
  const [spreadsheet, setSpreadsheet] = useState<CashFlowSummarySpreadsheet | null>(null);

  const rowData: CashFlowSummaryRowData[] = useMemo(() => {
    const fundIds = new Set<string>([
      ...Object.keys(fundsGrossIrr ?? {}),
      ...Object.keys(fundsInvestments ?? {}),
      ...Object.keys(fundsProceeds ?? {}),
    ]);

    const rows: CashFlowSummaryRowData[] = Array.from(fundIds)
      .map(fundId => {
        const investments = fundsInvestments[fundId] ?? 0;
        const proceeds = fundsProceeds[fundId] ?? 0;
        const grossIrr = fundsGrossIrr[fundId] ?? null;
        return {
          id: `fund-${fundId}`,
          name: fundIdToName[Number(fundId)] || `Fund ${fundId}`,
          investments,
          proceeds,
          total: investments + proceeds,
          grossIrr,
          isTotal: false,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    const firmHasData =
      !isNil(firmGrossIrr)
      || firmInvestments !== 0
      || firmProceeds !== 0
      || rows.length > 0;

    if (firmHasData) {
      rows.push({
        id: 'firm-total',
        name: firmName,
        investments: firmInvestments,
        proceeds: firmProceeds,
        total: firmInvestments + firmProceeds,
        grossIrr: firmGrossIrr,
        isTotal: true,
      });
    }

    return rows;
  }, [
    fundsGrossIrr,
    fundsInvestments,
    fundsProceeds,
    firmGrossIrr,
    firmInvestments,
    firmProceeds,
    fundIdToName,
    firmName,
  ]);

  const rowConfig = useMemo(() => CashFlowSummaryRowConfig(rowData), [rowData]);
  const columns = useMemo(() => getCashFlowSummaryColumns(), []);

  useEffect(() => {
    if (rowData.length === 0) {
      setSpreadsheet(null);
      return;
    }

    setSpreadsheet(
      new SpreadsheetConfig<
        CashFlowSummaryInitialObject,
        CashFlowSummaryColumnDefinition,
        CashFlowSummaryTableData,
        CashFlowSummaryCellData
      >({
        columns,
        name: 'CashFlowSummary',
        parser: CashFlowSummaryParser,
        rowConfig,
        tableData: { columns },
        format: '',
        showToolbar: false,
        allowDeleteRow: false,
        allowAddRow: false,
      })
    );
  }, [rowData, columns, rowConfig]);

  return {
    spreadsheet,
    hasData: rowData.length > 0,
  };
};
